import { MarketTypes } from "app/utils/const";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import storage from "redux-persist/lib/storage";
import type {
  EventMarketPeriodType,
  EventMarketType,
  EventRunnerType,
  EventType,
} from "../types/eventType";
export class BetSlipHelper {
  static formatMarketName = (
    betSlipMarketType,
    playerPropsFormattedName,
    formattedName
  ) => {
    if (
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_ASSISTS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_SHOTS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_GOALS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_POINTS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_BLOCKS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_STEALS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_REBOUNDS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_RUSHING_YARDS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_RUSHING_ATTEMPTS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_RECEIVING_YARDS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_RECEPTIONS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_PASSING_YARDS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_PASSING_ATTEMPTS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_PASS_COMPLETIONS_ROLLING ||
      betSlipMarketType ===
        MarketTypes.PLAYER_TOTAL_PASSING_TOUCHDOWNS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_ASSISTS_ROLLING ||
      betSlipMarketType === MarketTypes.PLAYER_TOTAL_POINTS_ROLLING
    ) {
      return playerPropsFormattedName;
    } else {
      return formattedName;
    }
  };
  /**
   * finds market that is containing given runner
   * and modifies event to have that one market
   * finds runner and modifies market to that one runner
   */
  static prepareBetSlip(event: EventType, runner: EventRunnerType): any {
    // if not cloned deep, we would override
    // deeper object references like markets or runners
    let ev = cloneDeep(event);
    let finalMarket: EventMarketType | null = null;
    let finalRunner: EventRunnerType | null = null;

    if (ev.AlternativeMarketPeriods != null) {
    } else if (ev.marketPeriods != null) {
      ev.marketPeriods.forEach(marketPeriod => {
        marketPeriod.marketTypes.forEach(marketType => {
          marketType.markets.forEach(market => {
            const foundRunner =
              market.runners !== undefined
                ? market.runners.find(r => r.id === runner.id)
                : null;

            if (foundRunner != null) {
              finalMarket = market;
              // use runner from deepCopied object
              finalRunner = foundRunner;
            }
          });
        });
      });
    }

    if (finalMarket != null) {
      finalMarket.runners = finalRunner != null ? [finalRunner] : [];
    }

    ev.markets = [finalMarket];
    return ev;
  }

  static prepareBetSlipWithMarketPeriod(
    event: EventType,
    marketPeriod: EventMarketPeriodType,
    runner: EventRunnerType
  ): any {
    // if not cloned deep, we would override
    // deeper object references like markets or runners
    let ev = cloneDeep(event);
    let mp = cloneDeep(marketPeriod);
    let finalMarket: EventMarketType | null = null;
    let finalRunner: EventRunnerType | null = null;
    mp.marketTypes.forEach(marketType => {
      marketType.markets.forEach(market => {
        const foundRunner =
          market.runners !== undefined
            ? market.runners.find(r => r.id === runner.id)
            : null;

        if (foundRunner != null) {
          finalMarket = market;
          // use runner from deepCopied object
          finalRunner = foundRunner;
        }
      });
    });

    if (finalMarket != null) {
      finalMarket.runners = finalRunner != null ? [finalRunner] : [];
    }

    ev.markets = [finalMarket];
    return ev;
  }

  static getBetSlipMarket(event: EventType): null | EventMarketType {
    // Check that the markets are not empty
    if (!isEmpty(event.markets)) {
      // return the first market from the event
      return event.markets[0];
    }

    return null;
  }

  // get betslip market type
  static getBetSlipMarketType(event: EventType): null | string {
    // Check that the markets are not empty
    if (!isEmpty(event.markets)) {
      // return type for first market from the event
      return event.markets[0].type;
    }

    return null;
  }

  static getBetSlipOdd(event: EventType): null | EventRunnerType {
    // Check that the markets are not empty
    const betSlipMarket = this.getBetSlipMarket(event);

    if (betSlipMarket != null) {
      // Check that the runners are not empty
      if (!isEmpty(betSlipMarket.runners)) {
        // return the first runner from the event
        return betSlipMarket.runners[0];
      }
    }

    return null;
  }

  static getBetSlipOddFromPrice(betSlipPrice, priceViewMode) {
    if (priceViewMode === "AGGREGATED") {
      return betSlipPrice !== undefined && betSlipPrice.odds;
    } else if (priceViewMode === "SOURCE") {
      return betSlipPrice !== undefined && betSlipPrice.sourceOdds;
    }
  }

  // To handle edge case where deleting a betslip and quickly reloading the page
  // the betslip would reappear, because there is a delay associated with the persist plugin.
  // This helper function is used to directly update the betslip list in local storage.
  static clearLocalStorageBetSlipLists() {
    storage.getItem("persist:root").then(currentState => {
      if (currentState) {
        const parsedState = JSON.parse(currentState);
        // Set betSlipList and multiplesBetSlipList to empty arrays
        parsedState.betSlipListModel = JSON.stringify({
          ...JSON.parse(parsedState.betSlipListModel),
          betSlipList: [],
        });
        parsedState.multiplesBetSlipListModel = JSON.stringify({
          ...JSON.parse(parsedState.multiplesBetSlipListModel),
          multiplesBetSlipList: [],
        });
        storage.setItem("persist:root", JSON.stringify(parsedState));
      }
    });
  }
  static setLocalStorageBetSlipLists(
    betSlipListModel,
    multiplesBetSlipListModel,
    betBuilderModel
  ) {
    // Get persistPlugin state from local storage
    storage.getItem("persist:root").then(currentState => {
      if (currentState) {
        // Convert the current bet slip models to strings for storage
        const parsedState = JSON.parse(currentState);
        const betSlipListModelString = JSON.stringify(betSlipListModel);
        const multiplesBetSlipListModelString = JSON.stringify(
          multiplesBetSlipListModel
        );
        const betBuilderModelString = JSON.stringify(betBuilderModel);

        // Update the parsed state with the new bet slip models
        parsedState.betSlipListModel = betSlipListModelString;
        parsedState.multiplesBetSlipListModel = multiplesBetSlipListModelString;
        parsedState.betBuilderModel = betBuilderModelString;

        // Save the updated state back to local storage
        storage.setItem("persist:root", JSON.stringify(parsedState));
      }
    });
  }
}
